import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { GeofenceShape } from '@raad/geo';
import { REGIONS } from './regions';
import { usePublishTemplate, type PublishTemplateBody } from './usePublishTemplate';

/**
 * Naming and filing a drawn template (slice 24e).
 *
 * 🔴 THE LABEL IS COMPOSED SERVER-SIDE AND THIS FORM DOES NOT BUILD ONE. It
 * collects `name` and `city` separately and the API returns
 * `Mogadishu · Bakaara Depot`. Assembling it here would be the second place the
 * convention lives — the thing 24d's whole naming discipline exists to prevent,
 * and the reason nine hand-drawn zones are called Home/home/Wado/wado/wado2.
 *
 * 🔴 THE ALERT RULE IS A SUGGESTION, AND THE COPY SAYS SO. Slice 24c took
 * alerting off `purpose` so the customer owns it per zone; a template that
 * imposed its rule would put that weld straight back for every zone copied out
 * of the library. What is chosen here pre-fills the tenant's form and nothing
 * more.
 */

const PURPOSES = ['keep_in', 'no_go', 'location', 'poi'] as const;
const RULES = ['never', 'on_enter', 'on_exit', 'on_both'] as const;

/**
 * 🔴 BE EXACT ABOUT WHICH GUARD RUNS. The first version of this comment said
 * "matching CorridorShapeZ/CircleShapeZ server-side; below this the API
 * refuses" — and that was FALSE FOR CIRCLES. `CircleShapeZ.radiusM` is
 * `.int().positive()`, so the API would happily take a 2 m radius; only the
 * corridor schema carries a 5 m floor. Written in the same slice that quotes
 * the rule about never documenting a protection that does not run, and caught
 * by raad-reviewer.
 *
 * What is actually true: 5 is the CORRIDOR floor server-side and this form
 * applies it to both kinds as a product choice, so a circle is refused here and
 * would not be refused by the API.
 */
const MIN_SIZE_M = 5;

/**
 * The DoS ceiling, and it IS enforced server-side on both kinds — `ST_Buffer`
 * with an unbounded radius is the denial-of-service both schemas cap at 20 km.
 * Mirrored here so an over-large value is named in the field rather than coming
 * back as a generic "could not publish".
 */
const MAX_SIZE_M = 20_000;

/**
 * 🔴 A WARNING, NEVER A BLOCK (Mahad, 26 Aug).
 *
 * At FMC920 accuracy in dense urban Mogadishu, a corridor this narrow will alert
 * on GPS jitter — the vehicle never left the road, the fix did. That is worth
 * saying out loud and worth NOT deciding for the person drawing it: they know
 * the road, the schema does not. The old server floor of 20 m made this
 * judgement silently by refusing; now it is shown and the publish proceeds.
 */
const JITTER_WARN_BELOW_M = 25;

/**
 * Last size SUCCESSFULLY PUBLISHED in this session, per kind.
 *
 * Named precisely after review: it is written inside submit(), AFTER the
 * validity gate, so a value typed and never published does not seed the next
 * corridor. The property that matters is unchanged — the seed is never a
 * constant — but "typed" claimed more than the code does.
 *
 * 🔴 NOT A DEFAULT CONSTANT, and that distinction is the whole defect this
 * replaces. `DEFAULT_CORRIDOR_WIDTH_M = 400` was a number nobody chose, and
 * `Danwadaag Road` is stored at 400 m on production because of it — indexed by
 * a constant, and indistinguishable afterwards from a width somebody meant.
 *
 * Module-level rather than component state so it survives the panel unmounting
 * between draws, and deliberately NOT persisted: a value from last week is a
 * constant again, just a slower one.
 */
const lastSize: { circle?: number; corridor?: number } = {};

export function PublishTemplateForm({
  shape,
  onPublished,
}: {
  /** The drawn shape. `null` disables publishing — see below. */
  shape: GeofenceShape | null;
  onPublished: () => void;
}) {
  const { t } = useTranslation();
  const publish = usePublishTemplate();

  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [purpose, setPurpose] = useState<PublishTemplateBody['purpose']>('location');
  const [rule, setRule] = useState<PublishTemplateBody['suggestedAlertRule']>('never');
  const [notes, setNotes] = useState('');
  const [failed, setFailed] = useState(false);
  /** Radius for a circle, width for a corridor. Kept as a STRING so the field
   *  can legitimately be empty — a number state would need a sentinel, and the
   *  sentinel becomes the default this replaces. */
  const [size, setSize] = useState('');

  const sizeKind: 'circle' | 'corridor' | null =
    shape === null ? null : shape.kind === 'circle' ? 'circle' : shape.kind === 'corridor' ? 'corridor' : null;

  // When a new shape arrives, seed the field WITHOUT inventing a number.
  //   - a circle carries the radius the person just dragged, which is their own
  //     input, so it seeds from that;
  //   - a corridor has no drawn width, so it seeds from the last one THEY typed
  //     this session, or stays EMPTY and blocks publish until they type one.
  // 🔴 THE FULL SERIALISATION, NOT ITS LENGTH. The first version keyed on
  // `JSON.stringify(shape).length`, so two DIFFERENT circles whose JSON happened
  // to be the same length did not reseed the field — and the gate case for
  // 'the tool disarms' passed even with the disarm removed, because the size it
  // watched never changed. A weak key made a real assertion vacuous.
  const shapeKey = shape === null ? '' : JSON.stringify(shape);
  useEffect(() => {
    if (shape === null) return;
    if (shape.kind === 'circle') setSize(String(shape.radiusM));
    else if (shape.kind === 'corridor') setSize(lastSize.corridor === undefined ? '' : String(lastSize.corridor));
    else setSize('');
    // shapeKey changes only when a genuinely new shape lands, so typing into the
    // field is never clobbered by a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapeKey]);

  const sizeNum = Number.parseInt(size, 10);
  const sizeValid =
    sizeKind === null ||
    (Number.isFinite(sizeNum) && sizeNum >= MIN_SIZE_M && sizeNum <= MAX_SIZE_M);
  const sizeTooBig = sizeKind !== null && Number.isFinite(sizeNum) && sizeNum > MAX_SIZE_M;
  const showJitterWarning = sizeKind !== null && Number.isFinite(sizeNum) && sizeNum >= MIN_SIZE_M && sizeNum < JITTER_WARN_BELOW_M;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (shape === null || name.trim() === '' || !sizeValid) return;
    setFailed(false);

    // 🔴 THE TYPED NUMBER WINS, AND IT TRAVELS AS AN INTEGER.
    //
    // Not the radius terra-draw computed from the drag, not a constant, and
    // never a value re-derived from the buffered polygon. ADR 0010's round-trip
    // rule is about exactly this: the user set 7, so 7 is what must be stored
    // and what must come back — not 6.98 recovered from a 64-segment ring.
    const sized: GeofenceShape =
      shape.kind === 'circle'
        ? { ...shape, radiusM: sizeNum }
        : shape.kind === 'corridor'
          ? { ...shape, widthM: sizeNum }
          : shape;
    if (sizeKind !== null) lastSize[sizeKind] = sizeNum;

    try {
      await publish.mutateAsync({
        name: name.trim(),
        city: city.trim() === '' ? null : city.trim(),
        // '' is the "not filed yet" option, and it must reach the API as NULL —
        // an empty string would render as a region heading with no name on the
        // customer's side (slice 24f pinned this shape on both ends).
        region: region === '' ? null : region,
        purpose,
        suggestedAlertRule: rule,
        notes: notes.trim() === '' ? null : notes.trim(),
        shape: sized,
      });
      setName('');
      setCity('');
      setRegion('');
      setNotes('');
      onPublished();
    } catch {
      setFailed(true);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <label className="block">
        <span className="text-[11.5px] font-semibold text-raad-muted">
          {t('library.publish.name')}
        </span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          className="mt-1 min-h-10 w-full rounded-lg border border-raad-border bg-raad-input px-3 text-sm text-raad-text"
        />
      </label>

      {/*
        🔴 THE SIZE IS TYPED. It was a hardcoded 400 m, and `Danwadaag Road` sits
        on production at 400 m because of it — a number nobody chose, stored as
        though somebody had.

        `type="text"` with `inputMode="numeric"`, not `type="number"`: a number
        input silently swallows non-numeric keystrokes and, on some browsers,
        scroll-wheel over the field changes the value. For a field that decides
        where a fence ends, an unnoticed change is the failure mode to design
        out.

        Shown only for shapes that HAVE a size — a polygon's extent is its
        outline, and a size box beside one would be a control for nothing.
      */}
      {sizeKind !== null ? (
        <label className="block">
          <span className="text-[11.5px] font-semibold text-raad-muted">
            {sizeKind === 'circle' ? t('library.publish.radius') : t('library.publish.width')}
          </span>
          <input
            type="text"
            inputMode="numeric"
            data-testid="shape-size-input"
            value={size}
            onChange={(e) => setSize(e.target.value.replace(/[^0-9]/g, ''))}
            aria-invalid={!sizeValid}
            className="mt-1 min-h-10 w-full rounded-lg border border-raad-border bg-raad-input px-3 text-sm text-raad-text"
          />
          {!sizeValid ? (
            <span role="alert" className="mt-1 block text-[11px] text-raad-error">
              {sizeTooBig
                ? t('library.publish.sizeTooBig', { max: MAX_SIZE_M })
                : t('library.publish.sizeTooSmall', { min: MIN_SIZE_M })}
            </span>
          ) : null}
          {showJitterWarning ? (
            // 🔴 A WARNING, NOT A BLOCK. role="status", not "alert": this does not
            // stop anything, and announcing it as an error would misdescribe it.
            <span role="status" className="mt-1 block text-[11px] text-raad-warn">
              {t('library.publish.jitterWarning', { m: JITTER_WARN_BELOW_M })}
            </span>
          ) : null}
        </label>
      ) : null}

      <label className="block">
        <span className="text-[11.5px] font-semibold text-raad-muted">
          {t('library.publish.city')}
        </span>
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          maxLength={80}
          className="mt-1 min-h-10 w-full rounded-lg border border-raad-border bg-raad-input px-3 text-sm text-raad-text"
        />
      </label>

      {/*
        🔴 A SELECT, NOT A TEXT INPUT, AND NO "OTHER". 24f shipped region as free
        text and flagged the drift in the same breath. An escape hatch is how a
        controlled vocabulary decays back into what it replaced — it gets reached
        for exactly when someone is in a hurry, which is when the typo happens.
        An unlisted region needs a change to regions.ts, deliberately.
      */}
      <label className="block">
        <span className="text-[11.5px] font-semibold text-raad-muted">
          {t('library.publish.region')}
        </span>
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          className="mt-1 min-h-10 w-full rounded-lg border border-raad-border bg-raad-input px-3 text-sm text-raad-text"
        >
          <option value="">{t('library.publish.regionNone')}</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-[11.5px] font-semibold text-raad-muted">
          {t('library.publish.purpose')}
        </span>
        <select
          value={purpose}
          onChange={(e) => setPurpose(e.target.value as PublishTemplateBody['purpose'])}
          className="mt-1 min-h-10 w-full rounded-lg border border-raad-border bg-raad-input px-3 text-sm text-raad-text"
        >
          {PURPOSES.map((p) => (
            <option key={p} value={p}>
              {t(`library.purpose.${p}`)}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-[11.5px] font-semibold text-raad-muted">
          {t('library.publish.suggestedRule')}
        </span>
        <select
          value={rule}
          onChange={(e) => setRule(e.target.value as PublishTemplateBody['suggestedAlertRule'])}
          className="mt-1 min-h-10 w-full rounded-lg border border-raad-border bg-raad-input px-3 text-sm text-raad-text"
        >
          {RULES.map((r) => (
            <option key={r} value={r}>
              {t(`library.rule.${r}`)}
            </option>
          ))}
        </select>
      </label>
      <p className="text-[11px] text-raad-muted">{t('library.publish.ruleIsASuggestion')}</p>

      <label className="block">
        <span className="text-[11.5px] font-semibold text-raad-muted">
          {t('library.publish.notes')}
        </span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
          rows={2}
          className="mt-1 w-full rounded-lg border border-raad-border bg-raad-input px-3 py-2 text-sm text-raad-text"
        />
      </label>

      {failed ? (
        <p role="alert" className="text-sm text-raad-error">
          {t('library.publish.failed')}
        </p>
      ) : null}

      {/*
        Disabled until something has been drawn — and this is NOT the "no control
        for a feature that does not exist" case. The feature exists; the button
        is waiting on THIS form's own required input, the same way a submit
        waits on a required field. What that rule forbids is advertising a
        capability the product does not have.
      */}
      {/*
        🔴 `text-raad-onprimary`, NOT `text-white`. That token exists because
        #5b6cf5 is a mid-tone: white on it measures below 4.5:1, and the
        near-black #0b1020 clears it comfortably. The token was created so no
        component could hardcode a foreground and quietly lose contrast when the
        accent is retuned — and then this button hardcoded one anyway.

        It also read `bg-raad-admin`, which is not a token at all, so the button
        had NO FILL and the contrast question was moot: white text on the panel.
        Both fixed together, and `ci/check-tailwind-tokens.mjs` now fails the
        build on the class that was invisible.
      */}
      <button
        type="submit"
        disabled={shape === null || name.trim() === '' || !sizeValid || publish.isPending}
        className="min-h-10 w-full rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-50"
      >
        {publish.isPending ? t('library.publish.publishing') : t('library.publish.publish')}
      </button>
      {shape === null ? (
        <p className="text-[11px] text-raad-muted">{t('library.publish.drawFirst')}</p>
      ) : null}
    </form>
  );
}
